import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Mail, Eye, MousePointer, AlertTriangle, Ship, Users } from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const COLORS = ['hsl(199, 89%, 48%)', 'hsl(160, 84%, 39%)', 'hsl(24, 95%, 53%)', 'hsl(262, 83%, 58%)'];

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      const response = await api.get(`/analytics/dashboard?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  const { summary, vessels } = analytics;

  // Prepare chart data
  const performanceData = [
    { name: 'Sent', value: summary.totalSent, color: COLORS[0] },
    { name: 'Opened', value: summary.totalOpened, color: COLORS[1] },
    { name: 'Clicked', value: summary.totalClicked, color: COLORS[2] },
    { name: 'Bounced', value: summary.totalBounced, color: COLORS[3] },
  ];

  const rateData = [
    { name: 'Open Rate', rate: summary.openRate },
    { name: 'Click Rate', rate: summary.clickRate },
    { name: 'Bounce Rate', rate: summary.bounceRate },
  ];

  const vesselStatusData = [
    { name: 'Active', value: vessels.active, color: COLORS[1] },
    { name: 'Inactive', value: vessels.total - vessels.active, color: COLORS[3] },
  ];

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-4 justify-end">
        <div className="flex items-center gap-2">
          <Label>From:</Label>
          <Input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="w-40"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label>To:</Label>
          <Input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="w-40"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sent</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {summary.totalSent.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Mail className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {summary.totalCampaigns} campaigns
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Rate</p>
                <p className="text-3xl font-bold mt-1 text-chart-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {summary.openRate.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-chart-2/10 rounded-full">
                <Eye className="h-6 w-6 text-chart-2" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {summary.totalOpened.toLocaleString()} opens
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Click Rate</p>
                <p className="text-3xl font-bold mt-1 text-chart-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {summary.clickRate.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-chart-4/10 rounded-full">
                <MousePointer className="h-6 w-6 text-chart-4" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {summary.totalClicked.toLocaleString()} clicks
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bounce Rate</p>
                <p className="text-3xl font-bold mt-1 text-destructive" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {summary.bounceRate.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-destructive/10 rounded-full">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {summary.totalBounced.toLocaleString()} bounces
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Performance Chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Email Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217, 33%, 17%)" />
                  <XAxis 
                    dataKey="name" 
                    stroke="hsl(215, 20%, 65%)"
                    tick={{ fill: 'hsl(215, 20%, 65%)' }}
                  />
                  <YAxis 
                    stroke="hsl(215, 20%, 65%)"
                    tick={{ fill: 'hsl(215, 20%, 65%)' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(217, 71%, 7%)', 
                      border: '1px solid hsl(217, 33%, 17%)',
                      borderRadius: '4px'
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {performanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Rate Comparison Chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Rate Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rateData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217, 33%, 17%)" />
                  <XAxis 
                    type="number" 
                    domain={[0, 100]}
                    stroke="hsl(215, 20%, 65%)"
                    tick={{ fill: 'hsl(215, 20%, 65%)' }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category"
                    stroke="hsl(215, 20%, 65%)"
                    tick={{ fill: 'hsl(215, 20%, 65%)' }}
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(217, 71%, 7%)', 
                      border: '1px solid hsl(217, 33%, 17%)',
                      borderRadius: '4px'
                    }}
                    formatter={(value) => [`${value.toFixed(1)}%`, 'Rate']}
                  />
                  <Bar dataKey="rate" fill="hsl(199, 89%, 48%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vessel Status & Additional Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vessel Status Pie Chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Ship className="h-5 w-5" />
              Vessel Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vesselStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {vesselStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(217, 71%, 7%)', 
                      border: '1px solid hsl(217, 33%, 17%)',
                      borderRadius: '4px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center mt-4">
              <p className="text-2xl font-bold">{vessels.total}</p>
              <p className="text-sm text-muted-foreground">Total Vessels</p>
            </div>
          </CardContent>
        </Card>

        {/* Vessel Stats */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Vessel Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-3xl font-bold text-chart-2">{vessels.active}</p>
                <p className="text-sm text-muted-foreground">Active Vessels</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-3xl font-bold text-muted-foreground">{vessels.total - vessels.active}</p>
                <p className="text-sm text-muted-foreground">Inactive Vessels</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-3xl font-bold text-chart-3">{vessels.suppressed || 0}</p>
                <p className="text-sm text-muted-foreground">Suppressed</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-3xl font-bold text-destructive">{vessels.highBounce || 0}</p>
                <p className="text-sm text-muted-foreground">High Bounce</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h4 className="font-medium mb-2">Campaign Summary</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xl font-bold">{summary.totalCampaigns}</p>
                  <p className="text-xs text-muted-foreground">Total Campaigns</p>
                </div>
                <div>
                  <p className="text-xl font-bold">{summary.totalSent}</p>
                  <p className="text-xs text-muted-foreground">Emails Sent</p>
                </div>
                <div>
                  <p className="text-xl font-bold">
                    {summary.totalSent > 0 
                      ? Math.round(summary.totalSent / Math.max(summary.totalCampaigns, 1))
                      : 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg per Campaign</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
